import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Cloud, Clock, Newspaper, QrCode, Timer, Check, Plus, Minus,
  Tv, Wand2, Zap, Globe, Users, Star, MapPin, Sun,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { NexsoLogo } from "../components/app/NexsoLogo";
import { ContactSupportLink } from "../components/app/ContactSupport";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

// ============================================================================
// Local style helpers (using inline classes via Tailwind)
// Font stack: Outfit (headings), Manrope (body), Playfair Display italic (accent)
// ============================================================================
const headingFont = { fontFamily: "Outfit, sans-serif" };
const bodyFont    = { fontFamily: "Manrope, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

// ============================================================================

export default function Landing() {
  return (
    <div className="min-h-screen text-slate-900 relative overflow-x-hidden" style={{ background: "#FFFFFF", ...bodyFont }}>
      {/* Global ambient blobs */}
      <div aria-hidden="true" className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute -top-32 -left-32 w-[40rem] h-[40rem] rounded-full opacity-30" style={{ background: "radial-gradient(circle, #0EA5E9 0%, transparent 70%)", filter: "blur(80px)" }} />
        <div className="absolute top-[40%] right-[-10%] w-[34rem] h-[34rem] rounded-full opacity-25" style={{ background: "radial-gradient(circle, #38BDF8 0%, transparent 70%)", filter: "blur(80px)" }} />
        <div className="absolute bottom-[-10%] left-[20%] w-[36rem] h-[36rem] rounded-full opacity-20" style={{ background: "radial-gradient(circle, #6366F1 0%, transparent 70%)", filter: "blur(80px)" }} />
      </div>

      <div className="relative z-10">
        <Navbar />
        <Hero />
        <SocialProof />
        <AppsWidgets />
        <Templates />
        <HowItWorks />
        <Industries />
        <Comparison />
        <Pricing />
        <Testimonials />
        <FAQ />
        <FinalCTA />
        <Footer />
      </div>
    </div>
  );
}

// ============================================================================
// Navbar
// ============================================================================
function Navbar() {
  return (
    <header
      className="fixed top-0 inset-x-0 z-50 border-b backdrop-blur-xl"
      style={{ background: "rgba(255,255,255,0.85)", borderColor: "var(--border-light)" }}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <NexsoLogo size="md" />
          <span className="font-semibold text-xl tracking-tight text-slate-900" style={headingFont}>Nexso</span>
        </Link>
        <nav className="hidden md:flex items-center gap-9 text-sm text-slate-600">
          <a href="#how" className="hover:text-slate-900 transition-colors" data-testid="nav-how">How it works</a>
          <a href="#widgets" className="hover:text-slate-900 transition-colors" data-testid="nav-widgets">Apps</a>
          <a href="#templates" className="hover:text-slate-900 transition-colors" data-testid="nav-templates">Templates</a>
          <a href="#pricing" className="hover:text-slate-900 transition-colors" data-testid="nav-pricing">Pricing</a>
          <Link to="/download" className="hover:text-slate-900 transition-colors" data-testid="nav-download">Download</Link>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="nav-signin-link">
            <Button variant="ghost" className="rounded-full text-slate-700 hover:text-slate-900 hover:bg-slate-50">Sign in</Button>
          </Link>
          <Link to="/register" data-testid="nav-getstarted-link">
            <Button
              className="rounded-full bg-sky-500 hover:bg-sky-600 text-white px-5 shadow-md hover:shadow-lg transition-shadow"
            >
              Get started
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

// ============================================================================
// Hero — Big headline with italic-serif emphasis word + floating widget cards
// ============================================================================
function Hero() {
  return (
    <section className="relative pt-36 md:pt-44 pb-24 md:pb-32" data-testid="hero-section">
      {/* Dotted grid texture */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.07]"
        style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "28px 28px" }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 backdrop-blur-md text-xs text-sky-600 mb-7" data-testid="hero-badge">
            <span className="text-base leading-none">✨</span>
            <span className="font-medium tracking-wide">Remote digital signage · made in minutes</span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight font-semibold" style={headingFont}>
            Full control over<br/>
            your{" "}
            <span className="relative inline-block">
              <span style={accentFont} className="text-sky-600">digital signage</span>
              <svg viewBox="0 0 200 8" className="absolute -bottom-2 left-0 w-full" aria-hidden="true">
                <path d="M0 5 Q 50 0 100 4 T 200 3" stroke="rgb(216, 180, 254)" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.7" />
              </svg>
            </span>
          </h1>
          <p className="mt-8 text-base md:text-lg text-slate-900/65 max-w-xl leading-relaxed">
            Plug an Android stick or smart TV into your screen, enter a pairing code on your dashboard, then design and publish digital menus, ads, and live apps &mdash; weather, clocks, news tickers, QR codes &mdash; remotely. No on-site visits.
          </p>
          <div className="mt-10 flex flex-wrap gap-3" data-testid="hero-cta-group">
            <Link to="/register">
              <Button
                size="lg"
                className="rounded-full h-12 px-7 bg-sky-500 hover:bg-sky-600 text-white shadow-md hover:shadow-lg transition-shadow"
                data-testid="hero-cta-start"
              >
                Start free <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/tv">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full h-12 px-7 bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100 backdrop-blur-md"
                data-testid="hero-cta-tv"
              >
                Open TV display
              </Button>
            </Link>
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-x-7 gap-y-3 text-xs text-slate-900/40">
            <Pill text="No credit card" />
            <Pill text="Works on any screen" />
            <Pill text="Live updates" />
          </div>
        </div>

        {/* RIGHT — TV mockup with floating widgets */}
        <div className="lg:col-span-6 relative h-[560px] hidden md:block" data-testid="hero-visual">
          {/* TV mockup */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="absolute inset-0 m-auto w-[88%] h-[80%] rounded-3xl border border-slate-200 overflow-hidden shadow-2xl bg-white"
          >
            <img
              src="https://images.pexels.com/photos/315755/pexels-photo-315755.jpeg?auto=compress&cs=tinysrgb&w=1600"
              alt="Nexso signage on TV"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(15,23,42,0.4), rgba(14,165,233,0.25))" }} />
            <div className="absolute inset-0 flex flex-col p-8 text-white">
              <div className="text-[10px] uppercase tracking-[0.3em] text-sky-200 font-bold">Live on TV</div>
              <div className="mt-2 text-3xl font-bold tracking-tight" style={headingFont}>Today&apos;s Menu</div>
              <div className="mt-1 text-xs text-white/70">Fresh ingredients, handcrafted daily</div>
              <div className="mt-auto flex items-end justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/60">Pairing</div>
                  <div className="font-mono text-2xl font-bold text-white">K7DR4Z</div>
                </div>
                <div className="h-12 w-12 rounded-2xl bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/30">
                  <Zap className="h-5 w-5 text-white" />
                </div>
              </div>
            </div>
          </motion.div>

          {/* Floating widget cards — polished OptiSigns style */}
          <FloatingShell delay={0.3} className="absolute top-2 -left-6 w-52">
            <HeroWeatherCard />
          </FloatingShell>

          <FloatingShell delay={0.5} className="absolute top-24 -right-8 w-48">
            <HeroClockCard />
          </FloatingShell>

          <FloatingShell delay={0.7} className="absolute bottom-20 -left-10 w-56">
            <HeroQRCard />
          </FloatingShell>

          <FloatingShell delay={0.9} className="absolute -bottom-2 right-2 w-56">
            <HeroCountdownCard />
          </FloatingShell>
        </div>
      </div>
    </section>
  );
}

function FloatingShell({ children, className = "", delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.92 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4 + delay, repeat: Infinity, ease: "easeInOut" }}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

function HeroWeatherCard() {
  return (
    <div className="relative overflow-hidden rounded-2xl text-white p-4 shadow-2xl"
         style={{ background: "linear-gradient(135deg, #38BDF8 0%, #0EA5E9 55%, #0284C7 100%)" }}>
      <div aria-hidden="true" className="absolute -top-10 -right-10 w-32 h-32 rounded-full"
           style={{ background: "radial-gradient(circle, rgba(255,222,128,0.7), transparent 70%)" }} />
      <div className="relative flex items-center gap-1.5 text-xs font-semibold opacity-95">
        <MapPin className="h-3 w-3" /> New York
      </div>
      <div className="relative mt-3 flex items-end justify-between">
        <div>
          <div className="text-4xl font-extrabold leading-none">72°</div>
          <div className="text-[10px] font-medium mt-1.5 opacity-90 uppercase tracking-wider">Sunny</div>
        </div>
        <Sun className="h-10 w-10 opacity-95" strokeWidth={1.6} />
      </div>
    </div>
  );
}

function HeroClockCard() {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-2xl">
      <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-slate-500">Mon · Feb 22, 2026</div>
      <div className="mt-2 flex items-baseline gap-1">
        <div className="text-3xl font-extrabold tracking-tight text-slate-900 leading-none" style={{ fontFamily: "Outfit, sans-serif" }}>5:24</div>
        <div className="text-sm font-bold text-slate-500">PM</div>
      </div>
    </div>
  );
}

function HeroQRCard() {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-2xl flex items-center gap-3">
      <div className="h-14 w-14 bg-slate-50 rounded-md p-1.5 border border-slate-200 flex-shrink-0">
        <div className="h-full w-full grid grid-cols-5 gap-px">
          {Array.from({ length: 25 }).map((_, i) => (
            <div key={i} className="bg-slate-900" style={{ opacity: [0,4,6,8,9,12,13,16,18,19,20,24].includes(i) ? 1 : 0 }} />
          ))}
        </div>
      </div>
      <div>
        <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-sky-600">QR · Menu</div>
        <div className="text-sm font-bold text-slate-900 leading-tight mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>Scan to view<br/>the menu</div>
      </div>
    </div>
  );
}

function HeroCountdownCard() {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-2xl">
      <div className="text-[9px] uppercase tracking-[0.2em] font-bold text-sky-600 mb-2">Promo ends in</div>
      <div className="grid grid-cols-3 gap-2">
        {[["02","Days"],["14","Hrs"],["32","Min"]].map(([v,l]) => (
          <div key={l} className="text-center">
            <div className="text-2xl font-extrabold text-sky-600 leading-none" style={{ fontFamily: "Outfit, sans-serif" }}>{v}</div>
            <div className="text-[9px] font-bold uppercase text-slate-400 mt-1">{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Pill({ text }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
      {text}
    </span>
  );
}

// ============================================================================
// Social proof — marquee of muted text/logos
// ============================================================================
function SocialProof() {
  const trusted = ["Restaurants", "Cafés", "Retail", "Gyms", "Salons", "Real Estate", "Bars", "Bakeries", "Churches", "Schools"];
  return (
    <section className="py-12 border-y" style={{ borderColor: "rgba(255,255,255,0.06)" }} data-testid="social-proof-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-xs uppercase tracking-[0.3em] text-slate-900/40 text-center mb-7">Trusted by local businesses everywhere</div>
        <div className="flex flex-wrap justify-center gap-x-12 gap-y-4 text-slate-900/30 text-sm font-medium">
          {trusted.map((t) => <span key={t}>{t}</span>)}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Apps & Widgets — the killer feature, in a bento grid
// ============================================================================
function AppsWidgets() {
  const widgets = [
    { icon: Cloud,     name: "Weather",     desc: "Live local conditions, auto-updates every 15 min.",     num: "01", accent: "from-blue-500 to-purple-500", iconClass: "text-blue-300" },
    { icon: Clock,     name: "Clock & Date", desc: "Live time + day · 12h or 24h · seconds toggle.",        num: "02", accent: "from-sky-400 to-sky-600", iconClass: "text-sky-600" },
    { icon: Newspaper, name: "News Ticker",  desc: "Scrolling messages · daily specials · alerts.",         num: "03", accent: "from-pink-500 to-orange-500", iconClass: "text-sky-500" },
    { icon: QrCode,    name: "QR Code",      desc: "Menu URL · WiFi · loyalty signup · reservations.",      num: "04", accent: "from-indigo-500 to-purple-500", iconClass: "text-indigo-300" },
    { icon: Timer,     name: "Countdown",    desc: "Promo timers · happy hour ends · event countdowns.",    num: "05", accent: "from-purple-500 to-fuchsia-500", iconClass: "text-fuchsia-300" },
  ];
  return (
    <section id="widgets" className="py-24 md:py-32 relative" data-testid="apps-widgets-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="mb-16 max-w-3xl">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-4">Apps &amp; Widgets · New</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-semibold" style={headingFont}>
            Overlay live content<br/>
            with{" "}
            <span className="relative inline-block">
              <span style={accentFont} className="text-sky-600">widgets</span>
              <svg viewBox="0 0 120 8" className="absolute -bottom-2 left-0 w-full" aria-hidden="true">
                <path d="M0 5 Q 30 0 60 4 T 120 3" stroke="rgb(216, 180, 254)" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.7" />
              </svg>
            </span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-slate-900/60 leading-relaxed">
            Turn any TV into more than a static menu. Drop weather, clocks, news tickers, QR codes, countdown timers anywhere on the screen &mdash; no code, no developer.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {widgets.map((w) => {
            const Icon = w.icon;
            return (
              <div
                key={w.name}
                className="group relative rounded-3xl p-8 border backdrop-blur-xl overflow-hidden hover:-translate-y-1 transition-all"
                style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.08)" }}
                data-testid={`widget-card-${w.name.toLowerCase().replace(/[^a-z]/g,"")}`}
              >
                <div className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${w.accent} flex items-center justify-center mb-5 shadow-md`}>
                  <Icon className="h-6 w-6 text-slate-900" />
                </div>
                <div className="text-xl font-semibold tracking-tight mb-2" style={headingFont}>{w.name}</div>
                <div className="text-sm text-slate-900/55 leading-relaxed">{w.desc}</div>
                <div className="absolute bottom-4 right-6 text-6xl font-bold text-slate-900/[0.04] select-none" style={headingFont}>{w.num}</div>
                <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ boxShadow: "inset 0 0 0 1px rgba(14,165,233,0.4)" }} />
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center text-xs uppercase tracking-[0.3em] text-slate-900/30">
          Coming soon · News headlines · Stock ticker · Social feeds · Sports scores · Calendar
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Templates gallery — horizontal carousel of beautiful menu templates
// ============================================================================
function Templates() {
  const templates = [
    { name: "Today's Menu", color: "from-amber-500/30 to-red-500/30",     img: "https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=600&q=80" },
    { name: "Brew & Bake",  color: "from-orange-500/30 to-yellow-500/30", img: "https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=600&q=80" },
    { name: "Wood-Fired",   color: "from-red-500/30 to-purple-500/30",    img: "https://images.unsplash.com/photo-1565299507177-b0ac66763828?w=600&q=80" },
    { name: "Cocktail Hour",color: "from-purple-500/30 to-pink-500/30",   img: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?w=600&q=80" },
    { name: "Daily Bakes",  color: "from-orange-500/30 to-pink-500/30",   img: "https://images.unsplash.com/photo-1555507036-ab1f4038808a?w=600&q=80" },
    { name: "Beauty Menu",  color: "from-pink-500/30 to-rose-500/30",     img: "https://images.unsplash.com/photo-1562322140-8baeececf3df?w=600&q=80" },
  ];
  return (
    <section id="templates" className="py-24 md:py-32" data-testid="templates-gallery-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10 mb-12">
        <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-4">24 ready templates</div>
        <h2 className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-semibold max-w-3xl" style={headingFont}>
          Bold, photo-rich{" "}
          <span style={accentFont} className="text-sky-600">templates</span>
          {" "}designed for screens.
        </h2>
      </div>
      <div className="overflow-hidden">
        <div className="flex gap-6 px-6 md:px-10 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" data-testid="templates-scroll">
          {templates.map((t, i) => (
            <div
              key={i}
              className="flex-shrink-0 w-80 aspect-video relative rounded-3xl overflow-hidden border snap-start"
              style={{ borderColor: "rgba(255,255,255,0.08)" }}
              data-testid={`template-card-${i}`}
            >
              <img src={t.img} alt={t.name} className="absolute inset-0 w-full h-full object-cover" />
              <div className={`absolute inset-0 bg-gradient-to-br ${t.color} mix-blend-overlay`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-1">Template · {String(i + 1).padStart(2, "0")}</div>
                <div className="text-2xl font-semibold tracking-tight" style={headingFont}>{t.name}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// How It Works — 3 steps with massive background numbers
// ============================================================================
function HowItWorks() {
  const steps = [
    { num: "01", title: "Plug & power on", desc: "Connect an Android stick, Fire TV, or smart TV browser to your screen. No software install." },
    { num: "02", title: "Enter the code",  desc: "Your TV shows a 6-digit pairing code. Type it on your phone or laptop dashboard. Done." },
    { num: "03", title: "Design & publish", desc: "Pick a template, drag in photos, drop widgets, hit save. Every TV updates live in seconds." },
  ];
  return (
    <section id="how" className="py-24 md:py-32 relative" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="mb-16 max-w-2xl">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-4">How it works</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-semibold" style={headingFont}>
            From box to broadcast in{" "}
            <span style={accentFont} className="text-sky-600">three steps</span>.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {steps.map((s) => (
            <div
              key={s.num}
              className="relative rounded-3xl p-8 border backdrop-blur-xl overflow-hidden min-h-[260px]"
              style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.08)" }}
              data-testid={`step-${s.num}`}
            >
              <div className="absolute -top-4 right-4 text-[160px] font-bold leading-none text-slate-900/[0.04] select-none" style={accentFont}>{s.num}</div>
              <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-6">Step {s.num}</div>
              <div className="text-2xl font-semibold tracking-tight mb-3" style={headingFont}>{s.title}</div>
              <div className="text-sm text-slate-900/55 leading-relaxed">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Industries — dense control-room grid
// ============================================================================
function Industries() {
  const inds = [
    { n: "Restaurants", desc: "Menus, specials" },
    { n: "Cafés",       desc: "Drinks, hours" },
    { n: "Retail",      desc: "Flash sales" },
    { n: "Salons",      desc: "Service prices" },
    { n: "Fitness",     desc: "Class schedules" },
    { n: "Real Estate", desc: "Open houses" },
    { n: "Bars",        desc: "Happy hour" },
    { n: "Bakeries",    desc: "Daily bakes" },
    { n: "Healthcare",  desc: "Lobby info" },
    { n: "Offices",     desc: "Team news" },
    { n: "Churches",    desc: "Service times" },
    { n: "Schools",     desc: "Lunch menus" },
  ];
  return (
    <section className="py-24 md:py-32" data-testid="industries-grid-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="mb-14 max-w-2xl">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-4">Built for every business</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-semibold" style={headingFont}>
            Templates &amp; widgets tuned to{" "}
            <span style={accentFont} className="text-sky-600">your industry</span>.
          </h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {inds.map((s, i) => (
            <div
              key={i}
              className="rounded-2xl p-4 border transition-all cursor-default hover:border-sky-300 hover:bg-slate-50"
              style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.08)" }}
              data-testid={`industry-${s.n.toLowerCase()}`}
            >
              <div className="text-base font-semibold tracking-tight" style={headingFont}>{s.n}</div>
              <div className="text-[11px] text-slate-900/40 mt-1">{s.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Comparison Table — Nexso highlighted vs SmartSign2Go vs Mvix
// ============================================================================
function Comparison() {
  const rows = [
    ["Price (1 screen)",    "$9.99 / mo",      "$19+ / mo",      "Hardware-required"],
    ["Extra screens",       "$4.99 / mo",      "$19+ / mo each", "Per-device license"],
    ["TV stick required",   "No · any browser","Often required", "Proprietary player"],
    ["Setup time",          "Under 5 minutes", "30+ minutes",    "Days · IT install"],
    ["Apps & Widgets",      "Built-in",        "Pro tier",       "Enterprise tier"],
    ["Canva uploads",       "Full-screen",     "Limited",        "Add-on"],
    ["Background removal",  "Built-in",        "External",       "External"],
    ["Free trial",          "7 days · no card",  "14-day trial",   "Demo only"],
  ];
  return (
    <section className="py-24 md:py-32" data-testid="comparison-table-section">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-4">Why Nexso</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-semibold" style={headingFont}>
            Same power.{" "}
            <span style={accentFont} className="text-sky-600">Half the price.</span>
          </h2>
        </div>
        <div className="rounded-3xl overflow-hidden border backdrop-blur-xl" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.08)" }}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                  <th className="text-left px-6 py-5 text-xs uppercase tracking-[0.2em] text-slate-900/40 font-medium">Feature</th>
                  <th className="text-center px-6 py-5 font-semibold relative" style={headingFont}>
                    <span className="relative inline-flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full bg-purple-400" /> Nexso
                    </span>
                  </th>
                  <th className="text-center px-6 py-5 text-slate-900/50 font-medium" style={headingFont}>SmartSign2Go</th>
                  <th className="text-center px-6 py-5 text-slate-900/50 font-medium" style={headingFont}>Mvix</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([f, n, s, m], i) => (
                  <tr key={i} className="border-b last:border-b-0" style={{ borderColor: "rgba(255,255,255,0.04)" }}>
                    <td className="px-6 py-4 text-slate-900/80">{f}</td>
                    <td className="px-6 py-4 text-center text-sky-600 font-semibold">{n}</td>
                    <td className="px-6 py-4 text-center text-slate-900/40">{s}</td>
                    <td className="px-6 py-4 text-center text-slate-900/40">{m}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Pricing — Free + Pro with monthly/yearly toggle
// ============================================================================
function Pricing() {
  const [yearly, setYearly] = useState(false);
  const proPrice = yearly ? 7.99 : 9.99;
  const extra = yearly ? 3.99 : 4.99;
  return (
    <section id="pricing" className="py-24 md:py-32" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-4">Pricing</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-semibold" style={headingFont}>
            Simple pricing.{" "}
            <span style={accentFont} className="text-sky-600">Pay for screens</span>, not seats.
          </h2>
          <div className="inline-flex items-center gap-1 mt-8 p-1 rounded-full border" style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.08)" }}>
            <button
              onClick={() => setYearly(false)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${!yearly ? "bg-sky-500 text-white shadow-md" : "text-slate-900/60 hover:text-slate-900"}`}
              data-testid="pricing-toggle-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setYearly(true)}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${yearly ? "bg-sky-500 text-white shadow-md" : "text-slate-900/60 hover:text-slate-900"}`}
              data-testid="pricing-toggle-yearly"
            >
              Yearly <span className="text-[10px] ml-1 text-sky-600">−20%</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {/* Trial */}
          <div className="rounded-3xl p-8 border backdrop-blur-xl" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.08)" }} data-testid="pricing-card-free">
            <div className="text-xs uppercase tracking-[0.3em] text-slate-900/40 mb-4">7-Day Free Trial</div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-5xl font-semibold tracking-tight" style={headingFont}>$0</span>
              <span className="text-sm text-slate-900/40">/ 7 days</span>
            </div>
            <div className="text-sm text-slate-900/55 mb-8">Full Pro access. No credit card required.</div>
            <ul className="space-y-3 text-sm text-slate-900/75 mb-8">
              {["1 screen during trial","All 24 templates &amp; Canva upload","Apps &amp; Widgets included","Image background removal","Screen groups &amp; scheduling","No watermark on your TVs"].map((f, i) => (
                <li key={i} className="flex items-start gap-2.5"><Check className="h-4 w-4 mt-0.5 text-purple-400 flex-shrink-0" /><span dangerouslySetInnerHTML={{ __html: f }} /></li>
              ))}
              <li className="flex items-start gap-2.5 text-sky-600/80 pt-1"><span className="h-4 w-4 mt-0.5 flex-shrink-0 text-center text-xs">+</span><span>Add a card after 7 days to keep your screens live</span></li>
            </ul>
            <Link to="/register" data-testid="pricing-free-cta">
              <Button size="lg" variant="outline" className="w-full rounded-full h-12 bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100 backdrop-blur-md">
                Start 7-day trial
              </Button>
            </Link>
          </div>

          {/* Pro */}
          <div
            className="rounded-3xl p-8 border backdrop-blur-xl relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, rgba(14,165,233,0.08), rgba(56,189,248,0.04))", borderColor: "rgba(14,165,233,0.4)", boxShadow: "0 10px 30px rgba(15,23,42,0.06)" }}
            data-testid="pricing-card-pro"
          >
            <div className="absolute top-0 right-0 px-4 py-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold bg-gradient-to-r from-sky-400 to-sky-600 text-slate-900 rounded-bl-2xl">
              Most popular
            </div>
            <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-4">Pro</div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="text-5xl font-semibold tracking-tight" style={headingFont}>${proPrice}</span>
              <span className="text-sm text-slate-900/40">/ {yearly ? "mo paid yearly" : "month"}</span>
            </div>
            <div className="text-sm text-sky-600 mb-1">Includes 1 screen.</div>
            <div className="text-sm text-slate-900/55 mb-8">+ ${extra}/{yearly ? "mo" : "month"} per additional screen.</div>
            <ul className="space-y-3 text-sm text-slate-900/85 mb-8">
              {["Everything in the trial","No Nexso watermark on your TVs","Unlimited screens (pay per device)","Screen groups &amp; scheduling","Priority email support"].map((f, i) => (
                <li key={i} className="flex items-start gap-2.5"><Check className="h-4 w-4 mt-0.5 text-purple-400 flex-shrink-0" /><span dangerouslySetInnerHTML={{ __html: f }} /></li>
              ))}
            </ul>
            <Link to="/register" data-testid="pricing-pro-cta">
              <Button size="lg" className="w-full rounded-full h-12 bg-sky-500 hover:bg-sky-600 text-white shadow-md hover:shadow-lg">
                Get Pro
              </Button>
            </Link>
          </div>
        </div>

        {/* Nexso Stick promo callout */}
        <div className="mt-10 max-w-4xl mx-auto" data-testid="nexso-stick-promo">
          <div className="rounded-3xl p-7 md:p-9 border backdrop-blur-xl relative overflow-hidden flex flex-col md:flex-row items-start md:items-center gap-6 md:gap-10"
               style={{ background: "linear-gradient(120deg, rgba(14,165,233,0.08), rgba(56,189,248,0.04))", borderColor: "rgba(56,189,248,0.35)", boxShadow: "0 10px 30px rgba(15,23,42,0.06)" }}>
            <div className="flex-shrink-0 h-16 w-16 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center shadow-lg">
              <Tv className="h-7 w-7 text-slate-900" />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.3em] text-sky-500 mb-2">Hardware bundle</div>
              <h3 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2" style={headingFont}>
                Buy a Nexso Stick &mdash; get{" "}
                <span style={accentFont} className="text-sky-500">1 month free</span>.
              </h3>
              <p className="text-sm md:text-base text-slate-900/65 leading-relaxed max-w-2xl">
                Every Nexso Stick ships with a unique promo code inside the box. Redeem it in your dashboard to add 30 days of Pro on top of your 7-day trial. Plug, pair, and you&apos;re live in minutes.
              </p>
            </div>
            <Link to="/register" className="flex-shrink-0" data-testid="nexso-stick-cta">
              <Button size="lg" className="rounded-full h-12 px-7 bg-sky-500 hover:bg-sky-600 text-white shadow-md hover:shadow-lg transition-shadow">
                Redeem code <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>

        <p className="text-center mt-8 text-xs text-slate-900/40">Prices in USD. Cancel anytime from your dashboard. Billed via Stripe.</p>
      </div>
    </section>
  );
}

// ============================================================================
// Testimonials — quick grid of glass cards
// ============================================================================
function Testimonials() {
  const items = [
    { q: "Set up four TVs across two cafés in a single afternoon. The widgets are a chef's kiss.", n: "Maya R.", r: "Café owner · Brooklyn" },
    { q: "Updating prices used to mean reprinting everything. Now I change one number and it's live in 5 seconds.", n: "Carlos M.", r: "Restaurant owner · Miami" },
    { q: "The weather + countdown widgets on our gym TVs made class signups go up. Worth every dollar.", n: "Jen T.", r: "Studio owner · Austin" },
  ];
  return (
    <section className="py-24 md:py-32" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-14">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-4">Testimonials</div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-semibold" style={headingFont}>
            What owners{" "}
            <span style={accentFont} className="text-sky-600">say.</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {items.map((t, i) => (
            <div
              key={i}
              className="rounded-3xl p-7 border backdrop-blur-xl"
              style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.08)" }}
              data-testid={`testimonial-${i}`}
            >
              <div className="flex gap-1 mb-5">
                {[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-purple-400 text-purple-400" />)}
              </div>
              <p className="text-base text-slate-900/85 leading-relaxed mb-6">&ldquo;{t.q}&rdquo;</p>
              <div className="text-sm font-semibold" style={headingFont}>{t.n}</div>
              <div className="text-xs text-slate-900/45">{t.r}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// FAQ — accordion
// ============================================================================
function FAQ() {
  const qa = [
    { q: "Do I need any hardware?", a: "No. Any device with a web browser works — Android stick, Fire TV, smart TV, Chromecast with a launcher, even a laptop on HDMI. We don't sell or require proprietary hardware." },
    { q: "How fast do updates appear on the TV?", a: "Within 5 seconds. Every paired TV polls the cloud for changes; the moment you save a design, every connected screen updates." },
    { q: "Can I use my Canva designs?", a: "Yes. Pick the 'Upload your Canva design' template, export 1920×1080 PNG/JPG from Canva, and drop it in. We auto-fit it to any aspect ratio." },
    { q: "How does the free trial work?", a: "Every new account gets 7 days of full Pro access — 1 paired screen, all 24 templates, all widgets, no watermark. No credit card required. Add a card before day 7 to keep your screens live." },
    { q: "Can I cancel anytime?", a: "Anytime, from the Billing page. Your subscription stops at the end of the current billing period; nothing is deleted." },
  ];
  return (
    <section className="py-24 md:py-32" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-4">FAQ</div>
          <h2 className="text-4xl md:text-5xl leading-[1.1] tracking-tight font-semibold" style={headingFont}>
            Frequently asked{" "}
            <span style={accentFont} className="text-sky-600">questions</span>.
          </h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {qa.map((item, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="rounded-2xl border backdrop-blur-xl px-5 data-[state=open]:border-sky-300"
              style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.08)" }}
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="text-left text-base font-semibold hover:no-underline" style={headingFont}>
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm text-slate-900/60 leading-relaxed pb-4">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

// ============================================================================
// Final CTA
// ============================================================================
function FinalCTA() {
  return (
    <section className="py-24 md:py-32" data-testid="final-cta-section">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div
          className="rounded-3xl p-12 md:p-16 border backdrop-blur-xl relative overflow-hidden text-center"
          style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(14,165,233,0.3)", boxShadow: "0 10px 30px rgba(15,23,42,0.06)" }}
        >
          <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
            <div className="absolute top-0 left-1/4 w-64 h-64 rounded-full opacity-30" style={{ background: "radial-gradient(circle, #0EA5E9 0%, transparent 70%)", filter: "blur(60px)" }} />
            <div className="absolute bottom-0 right-1/4 w-64 h-64 rounded-full opacity-30" style={{ background: "radial-gradient(circle, #38BDF8 0%, transparent 70%)", filter: "blur(60px)" }} />
          </div>
          <div className="relative">
            <h2 className="text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-semibold" style={headingFont}>
              Stop printing menus.<br/>
              Start{" "}
              <span style={accentFont} className="text-sky-600">publishing them.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-900/60 max-w-xl mx-auto">
              Try Nexso free on your first screen. No credit card, no install, no contract.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <Link to="/register">
                <Button size="lg" className="rounded-full h-12 px-7 bg-sky-500 hover:bg-sky-600 text-white shadow-md hover:shadow-lg" data-testid="final-cta-start">
                  Get started free <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-7 bg-slate-50 border-slate-200 text-slate-900 hover:bg-slate-100 backdrop-blur-md" data-testid="final-cta-signin">
                  Sign in
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================================================
// Footer
// ============================================================================
function Footer() {
  return (
    <footer className="pt-16 pb-10 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }} data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid md:grid-cols-4 gap-10 mb-12">
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-2.5 mb-4">
              <NexsoLogo size="md" />
              <span className="font-semibold text-xl tracking-tight" style={headingFont}>Nexso</span>
            </Link>
            <p className="text-sm text-slate-900/45 max-w-md leading-relaxed">
              Remote digital signage for restaurants, retail, and every screen in between. Built for owners who hate the headache.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-slate-900/40 mb-4" style={headingFont}>Product</div>
            <ul className="space-y-2 text-sm text-slate-900/60">
              <li><a href="#how" className="hover:text-slate-900 transition-colors">How it works</a></li>
              <li><a href="#widgets" className="hover:text-slate-900 transition-colors">Apps &amp; widgets</a></li>
              <li><a href="#templates" className="hover:text-slate-900 transition-colors">Templates</a></li>
              <li><a href="#pricing" className="hover:text-slate-900 transition-colors">Pricing</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-slate-900/40 mb-4" style={headingFont}>Company</div>
            <ul className="space-y-2 text-sm text-slate-900/60">
              <li><Link to="/login" className="hover:text-slate-900 transition-colors">Sign in</Link></li>
              <li><Link to="/register" className="hover:text-slate-900 transition-colors">Get started</Link></li>
              <li><Link to="/download" className="hover:text-slate-900 transition-colors">Download TV app</Link></li>
              <li><Link to="/tv" className="hover:text-slate-900 transition-colors">Open TV display</Link></li>
              <li><ContactSupportLink className="hover:text-slate-900 transition-colors" testId="footer-contact-support">Contact support</ContactSupportLink></li>
              <li><Link to="/terms" className="hover:text-slate-900 transition-colors" data-testid="footer-terms-link">Terms of Service</Link></li>
              <li><Link to="/privacy" className="hover:text-slate-900 transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="pt-8 border-t flex flex-col md:flex-row items-start md:items-center justify-between gap-4" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <div className="text-xs text-slate-900/35">© {new Date().getFullYear()} Nexso. All rights reserved.</div>
          <div className="text-[8rem] md:text-[10rem] font-bold leading-none tracking-tighter text-slate-900/[0.04] select-none -mb-6" style={headingFont}>
            NEXSO
          </div>
        </div>
      </div>
    </footer>
  );
}
